import { clsx } from "clsx";
import React, { createContext, useContext } from "react";

import { useDebounce, useScrollDirection } from "@chef/hooks";
import { FEATURE_FLAGS } from "@chef/constants";
import { useFlag } from "@chef/feature-flags";
import { useMobileApp } from "@chef/feature-mobile-app";

interface StickyProps {
  children: React.ReactNode;
  anchor?: "top" | "bottom";
  float?: boolean;
  className?: string;
}

interface StickyContextProps {
  isFloating: boolean;
}

const TRESHOLD = 120;

const StickyContext = createContext<StickyContextProps>({
  isFloating: false,
});

export const useSticky = () => useContext(StickyContext);

export const Sticky = ({
  children,
  anchor = "top",
  float = false,
  className,
}: StickyProps) => {
  const { isAtTop, isAtBottom } = useScrollDirection(TRESHOLD);
  const { isApp } = useMobileApp();

  const { enabled: isWeekEditorRedesignEnabled } = useFlag(
    FEATURE_FLAGS.WEEK_EDITOR_REDESIGN,
  );

  let _isAtEdge: boolean;

  if (anchor === "top") {
    _isAtEdge = isAtTop;
  } else if (anchor === "bottom") {
    _isAtEdge = isAtBottom;
  } else {
    _isAtEdge = false;
  }

  const isAtEdge = useDebounce(_isAtEdge, 100);

  return (
    <div
      className={clsx(
        anchor === "bottom" && "lg:hidden print:hidden",
        className,
      )}
    >
      {anchor === "top" && (
        <div
          key="sticky-dummy"
          aria-hidden="true"
          className={clsx("h-15", isWeekEditorRedesignEnabled && "lg:h-30")}
        />
      )}
      {anchor === "bottom" && (
        <div key="sticky-dummy" aria-hidden="true" className="h-18" />
      )}
      <div
        key="sticky-content"
        className={clsx("fixed left-0 right-0 z-40", {
          "top-0 bg-background":
            (anchor === "top" && float && isAtEdge) ||
            (anchor === "top" && !float),
          "px-5 lg:px-7": anchor === "bottom",
          "bottom-5": anchor === "bottom" && isApp,
          "bottom-2": anchor === "bottom" && !isApp,
          "top-2 mx-5 lg:mx-9": anchor === "top" && float && !isAtEdge,
          "transition-all duration-300 ease-in-out": float,
        })}
      >
        <div
          className={clsx("w-full frosted-glass", {
            "px-4 lg:px-6 shadow page-max-width":
              anchor === "top" && float && !isAtEdge,
            "shadow-fab": !float,
            "rounded-full": (float && !isAtEdge) || anchor === "bottom",
          })}
        >
          <StickyContext.Provider value={{ isFloating: float && !isAtEdge }}>
            {children}
          </StickyContext.Provider>
        </div>
      </div>
    </div>
  );
};
